import { Box, Button, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getAnotherPhishingEmailSubject, getComposedPhishingEmail } from '@/api/open-ai/get-composed-phishing-email'
import { getComposedPhishingSms } from '@/api/open-ai/get-composed-phishing-sms'
import questionImg from '@/assets/images/popup/question.svg'
import InfoTooltip from '@/common/components/InfoTooltip/InfoTooltip'
import MobilePhonePreview from '@/common/components/MobilePhonePreview'
import SmsAppPreview from '@/common/components/MobilePhonePreview/SmsAppPreview'
import WhatsAppPreview from '@/common/components/MobilePhonePreview/WhatsAppPreview'
import Popup from '@/common/components/Popup'
import MainTab from '@/common/components/Tabs/MainTab'
import MainTabPage from '@/common/components/Tabs/MainTabPage'
import MainTabs from '@/common/components/Tabs/MainTabs'
import MainTabsWrapper from '@/common/components/Tabs/MainTabsWrapper'
import { theme } from '@/theme/theme'
import { PhishingSimulationVector } from '@/types/phishingSimulations'
import AppEmailForm from './AiForms/AppEmailForm'
import { HumanEmailForm } from './AiForms/HumanEmailForm'
import AiLoader from './components/AiLoader'
import EmailPlaceholder from './components/EmailPlaceholder'
import MonitorPreview from './components/MonitorPreview'
import MonitorPreviewIframe from './components/MonitorPreviewIframe'
import VectorToggleButtons from './components/VectorToggleButtons'
import { loadingPhrases } from './constants/loading-phrases'
import { SIMULATION_TYPES, useAiSimulationContext } from './state'
import { generateFindingsForSelectedItems, getItemsForDifficulty } from './utils/findingsUtils'

const LOAD_TIME = 10000

const MessageBuilder: FC = () => {
  const {
    currentStep,
    setCurrentStep,
    simulationType,
    setSimulationType,
    language,
    toneOfVoice,
    emailBody,
    senderName,
    senderPosition,
    instructions,
    vectorType,
    subjectOutput,
    setSubjectOutput,
    bodyOutput,
    setBodyOutput,
    designedEmail,
    appName,
    emailSenderSignature,
    customLogo,
    resetEmail,
    personalizedTemplate,
    findingsInstructions,
    difficultyLevel,
    setFindings,
    setFindingsInstructions,
    // iconTheme, not in use
  } = useAiSimulationContext()

  const theme = useTheme()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [subjectLoading, setSubjectLoading] = useState(false)
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | undefined>()
  const [randomPhrase, setRandomPhrase] = useState('')
  const [isGenerateClicked, setIsGenerateClicked] = useState(false)
  const [isPreviewInFullscreen, setIsPreviewInFullscreen] = useState(false)
  const [errorPopupOpen, setErrorPopupOpen] = useState(false)

  useEffect(() => {
    updateRandomPhrase(loading)
  }, [loading])

  const updateRandomPhrase = (loading: boolean) => {
    if (loading) {
      setRandomPhrase(getRandomPhrase())

      const id = setInterval(() => {
        setRandomPhrase(getRandomPhrase())
      }, LOAD_TIME)

      setIntervalId(id)
    } else {
      clearInterval(intervalId)
    }
  }

  useEffect(() => {
    setBodyOutput('')
    setSubjectOutput('')
    setIsGenerateClicked(false)
  }, [vectorType])

  useEffect(() => {
    updateFindings()
  }, [difficultyLevel])

  const updateFindings = () => {
    // items to add to the findings object saved in DB
    const selectedItems = getItemsForDifficulty(difficultyLevel, vectorType)
    setFindings(selectedItems)

    // the text to add to the prompt
    const findings = generateFindingsForSelectedItems(selectedItems)
    setFindingsInstructions(findings)
  }
  const getRandomPhrase = () => {
    const randomIndex = Math.floor(Math.random() * loadingPhrases.length)
    return loadingPhrases[randomIndex]
  }

  const refreshEmailSubject = async () => {
    setSubjectLoading(true)
    try {
      const newSubject = await getAnotherPhishingEmailSubject({ email_body: emailBody, different_than: subjectOutput })
      setSubjectOutput(newSubject)
    } catch (error) {
      setErrorPopupOpen(true)
    }
    setSubjectLoading(false)
  }

  const createMessage = async () => {
    setLoading(true)
    try {
      PhishingSimulationVector.Email === vectorType ? await generateEmail() : await generateSms()
    } catch (error) {
      setErrorPopupOpen(true)
    }
    setLoading(false)
  }

  const handleContinue = () => {
    setCurrentStep(currentStep + 1)
  }

  const generateEmail = async () => {
    const body =
      simulationType === SIMULATION_TYPES.HUMAN
        ? {
            language: language,
            tone_of_voice: toneOfVoice,
            sender_name: senderName,
            sender_position: senderPosition,
            body_instructions: emailBody + ', ' + instructions,
            logo_for_email_signature: customLogo || emailSenderSignature,
            personalize: personalizedTemplate,
          }
        : {
            app_name: appName,
            logo_url: customLogo,
            language: language,
            style: designedEmail ? 'designed' : 'textual',
            tone_of_voice: toneOfVoice,
            body_instructions: emailBody + ', ' + instructions,
            personalize: personalizedTemplate,
          }
    const email = await getComposedPhishingEmail({
      ...body,
      findings_instructions: findingsInstructions || '',
    })
    setBodyOutput(email.message)
    setSubjectOutput(email.subject)
  }

  const generateSms = async () => {
    const body =
      simulationType === SIMULATION_TYPES.HUMAN
        ? {
            language: language,
            tone_of_voice: toneOfVoice,
            sender_name: senderName,
            sender_position: senderPosition,
            body_instructions: emailBody + ', ' + instructions,
            personalize: personalizedTemplate,
          }
        : {
            app_name: appName,
            language: language,
            tone_of_voice: toneOfVoice,
            body_instructions: emailBody + ', ' + instructions,
            personalize: personalizedTemplate,
          }
    const sms = await getComposedPhishingSms({
      ...body,
      findings_instructions: findingsInstructions || '',
    })
    setBodyOutput(sms)
  }

  const enableGenerateButton = () => {
    if (loading) {
      return false
    }
    return simulationType === SIMULATION_TYPES.HUMAN
      ? language && toneOfVoice && emailBody && senderName && senderPosition
      : language && toneOfVoice && emailBody && appName
  }

  const monitorOrPhonePreview = () => {
    const isGenerated = bodyOutput && true
    return vectorType === PhishingSimulationVector.Email ? (
      <Box display="flex" flexDirection="column" alignItems="center">
        <MonitorPreview
          pageType="email"
          isLoading={loading}
          hasContent={!!isGenerated}
          setIsFullscreen={(isFullscreen) => setIsPreviewInFullscreen(isFullscreen)}
          isFullscreen={isPreviewInFullscreen}
          subject={subjectOutput}
          onSubjectRefresh={refreshEmailSubject}
          subjectLoading={subjectLoading}>
          {bodyOutput ? (
            <MonitorPreviewIframe text={bodyOutput} isModal={isPreviewInFullscreen} iFrameTitle="email-output" />
          ) : (
            <>
              {loading ? (
                <Box p={1}>
                  <Typography sx={{ textAlign: 'center', color: theme.palette.white }}>{randomPhrase}</Typography>
                  <AiLoader />
                </Box>
              ) : (
                <EmailPlaceholder isModal={isPreviewInFullscreen} />
              )}
            </>
          )}
        </MonitorPreview>
      </Box>
    ) : vectorType === PhishingSimulationVector.SMS ? (
      <Box style={{ width: '250px' }}>
        <MobilePhonePreview isLoading={loading} fullScreenButton>
          <SmsAppPreview sender={appName || senderName} message={bodyOutput} isHookBG={true} />
        </MobilePhonePreview>
      </Box>
    ) : (
      <Box style={{ width: '250px' }}>
        <MobilePhonePreview isLoading={loading} fullScreenButton>
          <WhatsAppPreview sender={appName || senderName} message={bodyOutput} />
        </MobilePhonePreview>
      </Box>
    )
  }

  return (
    <Box width={'100%'}>
      <MainTabsWrapper>
        <MainTabs
          value={simulationType}
          onChange={(_event, newValue) => {
            resetEmail()
            setSimulationType(newValue)
          }}
          indicatorColor="primary"
          textColor="primary">
          <MainTab disabled={loading} label="Impersonate an app" value={SIMULATION_TYPES.APP} />
          <MainTab disabled={loading} label="Impersonate a human" value={SIMULATION_TYPES.HUMAN} />
        </MainTabs>
      </MainTabsWrapper>
      <MainTabPage>
        <Box p={4}>
          <Box display="flex" flexDirection="row" justifyContent="flex-start" alignItems="flex-start" gap={3.25}>
            <Box display="flex" flexDirection="column" gap={4} flex={1}>
              <Box>
                <Box>
                  <Box display="flex" flexDirection="column" gap={1.5} height="fit-content">
                    <VectorToggleButtons disabled={loading} />
                    {simulationType === SIMULATION_TYPES.HUMAN ? (
                      <HumanEmailForm simulationVectorType={vectorType} />
                    ) : (
                      <AppEmailForm simulationVectorType={vectorType} />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Preview */}
            <Box display="flex" flexDirection="column" flex={1} alignItems="center" gap={theme.spacing(4)}>
              {monitorOrPhonePreview()}
              <AiDisclaimer />
            </Box>
          </Box>
          {/* Spacer */}
          <Box mt={2.5}></Box>

          {/* Action Buttons */}
          <Box display="flex" flexDirection="row" justifyContent="flex-start" alignItems="flex-start">
            <Box display="flex" flexDirection="row" justifyContent="flex-end" alignItems="center" width="50%">
              {!bodyOutput && !isGenerateClicked ? (
                <Button
                  disabled={!enableGenerateButton()}
                  onClick={(e) => {
                    createMessage()
                    e.stopPropagation()
                  }}
                  variant={'contained'}>
                  {t('simulationLibrary.ai.generate')}
                </Button>
              ) : (
                <Button
                  disabled={!enableGenerateButton()}
                  onClick={(e) => {
                    setBodyOutput('')
                    setIsGenerateClicked(true)
                    updateFindings()
                    createMessage()
                    e.stopPropagation()
                  }}
                  variant={'outlined'}>
                  {t('simulationLibrary.ai.regenerate')}
                </Button>
              )}
            </Box>

            <Box display="flex" flexDirection="row" justifyContent="flex-end" alignItems="center" width="50%">
              {bodyOutput && (
                <Box>
                  <Button onClick={handleContinue} variant={'contained'}>
                    {t('simulationLibrary.ai.continue')}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </MainTabPage>
      {/* Error Popup */}
      <Popup
        open={errorPopupOpen}
        onClose={() => {
          setErrorPopupOpen(false)
        }}
        onConfirm={() => {
          setBodyOutput('')
          setIsGenerateClicked(true)
          updateFindings()
          createMessage()
          setErrorPopupOpen(false)
        }}
        buttonLabels={{
          confirm: t('simulationLibrary.ai.regenerate'),
          cancel: t('simulationLibrary.ai.cancel'),
        }}
        icon={questionImg}>
        <Typography>{t('simulationLibrary.ai.errorMessage')}</Typography>
      </Popup>
    </Box>
  )
}

const AiDisclaimer = () => {
  const { t } = useTranslation()
  return (
    <Box display="flex" alignItems="center">
      <InfoTooltip
        color={theme.palette.blueDianne[900]}
        title={
          <Box padding={theme.spacing(0.5, 0)} gap={theme.spacing(1)}>
            <Typography fontWeight={theme.typography.fontWeightBold} fontSize={14}>
              {t('simulationLibrary.ai.disclaimerTooltip.title')}
            </Typography>
            <Typography lineHeight="normal" fontSize={14}>
              {t('simulationLibrary.ai.disclaimerTooltip.message')}
            </Typography>
          </Box>
        }
      />
      <Typography fontSize={14} fontWeight={theme.typography.fontWeightMedium}>
        {t('simulationLibrary.ai.disclaimer')}
      </Typography>
    </Box>
  )
}
export default MessageBuilder
